import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Paper, Typography, Modal, Button } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    display: "flex", // Standard flex layout
    flexDirection: "column",
    height: "100%", // Full height
    backgroundColor: theme.palette.type === 'light' ? '#f5f5f5' : theme.palette.background.default,
    padding: theme.spacing(4),
    overflowY: "auto", // Allow main container to scroll
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  gridContainer: { // Renamed from mainPaper
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  helpCard: { // Renamed from helpPaper
    position: 'relative',
    width: '100%',
    minHeight: '340px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: "16px", // standard rounded
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)", // subtle shadow
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // for sticky image
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
      borderColor: theme.palette.primary.main,
    },
  },
  videoThumbnail: {
    width: '100%',
    height: '200px', // Fixed height for consistency
    objectFit: 'cover',
  },
  cardContent: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  videoTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  videoDescription: {
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
  },
  videoModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoModalContent: {
    outline: 'none',
    width: '90%',
    maxWidth: 1024,
    aspectRatio: '16/9',
    position: 'relative',
    backgroundColor: 'black',
    borderRadius: "16px",
    overflow: 'hidden',
    boxShadow: theme.shadows[10],
  },
}));

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
  }, []); // list is stable

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const renderVideoModal = () => {
    return (
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          {selectedVideo && (
            <iframe
              style={{ width: "100%", height: "100%", border: "none" }}
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div className={classes.mainContainer}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>
            {i18n.t("helps.title")} ({records.length})
          </Typography>
          <Typography className={classes.subtitle}>
            Tutoriais e ajudas para você
          </Typography>
        </div>
      </div>

      <div className={classes.gridContainer}>
        {records.length > 0 ? records.map((record, key) => (
          <Paper key={key} className={classes.helpCard} onClick={() => openVideoModal(record.video)}>
            <img
              src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
              alt="Thumbnail"
              className={classes.videoThumbnail}
            />
            <div className={classes.cardContent}>
              <Typography variant="h6" className={classes.videoTitle}>
                {record.title}
              </Typography>
              <Typography variant="body2" className={classes.videoDescription}>
                {record.description}
              </Typography>
            </div>
          </Paper>
        )) : (
          <Typography variant="body1">Nenhum tutorial encontrado.</Typography>
        )}
      </div>
      {renderVideoModal()}
    </div>
  );
};

export default Helps;